import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { GitMergeIcon as GitMerge, DogIcon as Dog, DetectiveIcon as Detective, CheckCircleIcon as CheckCircle, TrashIcon as Trash } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { thumbUrl, formatDate } from "@/lib/utils-app";

/**
 * Admin: bulk duplicate-dog finder.
 *
 * Aggregates dogs by (name + breed + dob) and lets the admin one-click
 * merge the smaller group members into the chosen canonical. Canonical
 * defaults to the first (oldest) member — admin can swap with a click.
 */
export default function AdminDuplicateDogs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canonicalByGroup, setCanonicalByGroup] = useState({});
  const [merging, setMerging] = useState({});

  useEffect(() => {
    if (user && user.role !== "admin") navigate("/dashboard");
  }, [user, navigate]);

  const groupKey = (g) => `${g.name}::${g.breed}::${g.dob}`;

  const load = () => {
    setLoading(true);
    api.get("/admin/records/dogs/duplicates")
      .then((r) => {
        const gs = r.data?.groups || [];
        setGroups(gs);
        // Default canonical = first member of each group.
        const init = {};
        for (const g of gs) {
          init[groupKey(g)] = g.members[0]?.id;
        }
        setCanonicalByGroup(init);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const mergeOne = async (g, duplicateId) => {
    const canonical = canonicalByGroup[groupKey(g)];
    if (!canonical || canonical === duplicateId) {
      toast.error("Pick a different canonical first.");
      return;
    }
    setMerging((m) => ({ ...m, [duplicateId]: true }));
    try {
      const r = await api.post("/admin/records/dogs/merge", {
        canonical_id: canonical,
        duplicate_id: duplicateId,
      });
      const n = Object.values(r.data?.relinked || {}).reduce((a, x) => a + Number(x || 0), 0);
      toast.success(`Merged. Re-linked ${n} reference${n === 1 ? "" : "s"}.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Merge failed");
    } finally {
      setMerging((m) => ({ ...m, [duplicateId]: false }));
    }
  };

  const mergeAll = async (g) => {
    const canonical = canonicalByGroup[groupKey(g)];
    if (!canonical) return;
    const duplicates = g.members.filter((m) => m.id !== canonical);
    if (!duplicates.length) return;
    if (!window.confirm(`Merge all ${duplicates.length} duplicate(s) into the chosen canonical?`)) return;
    for (const d of duplicates) {
      // Sequential so audit log + reference re-pointing stay coherent.
      // eslint-disable-next-line no-await-in-loop
      await mergeOne(g, d.id);
    }
  };

  return (
    <AppShell>
      <div className="flex items-center gap-3 mb-2">
        <Detective size={28} weight="duotone" className="text-ink" />
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#8A6A00]">
          Admin · Data hygiene
        </p>
      </div>
      <h1 className="font-spectral font-light text-4xl sm:text-5xl tracking-tight leading-[1.05]">
        Duplicate dogs
      </h1>
      <p className="text-sm text-[#6B6B6B] mt-3 leading-relaxed max-w-3xl">
        Dogs with the same name, breed and date of birth — likely the same
        physical animal registered twice. Pick the canonical record, then
        merge the duplicate. All offspring / litter / pedigree references
        get re-pointed to the canonical automatically.
      </p>

      <div className="mt-8 flex items-center gap-3">
        <Button
          onClick={load}
          variant="outline"
          className="rounded-full"
          data-testid="duplicates-refresh"
        >
          Refresh
        </Button>
        <span className="text-xs text-[#6B6B6B]" data-testid="duplicates-summary">
          {loading
            ? "Scanning…"
            : `${(groups || []).length} duplicate group${(groups || []).length === 1 ? "" : "s"} found`}
        </span>
      </div>

      {!loading && groups && groups.length === 0 && (
        <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-xl p-6 flex items-center gap-3" data-testid="duplicates-none">
          <CheckCircle size={22} weight="fill" className="text-emerald-600" />
          <span className="text-sm text-emerald-900">No duplicate dogs detected. Registry looks clean.</span>
        </div>
      )}

      {!loading && groups && groups.length > 0 && (
        <div className="mt-8 space-y-6">
          {groups.map((g) => {
            const key = groupKey(g);
            const canonical = canonicalByGroup[key];
            return (
              <div key={key} className="bg-white border border-[#E5E5E5] rounded-xl p-5" data-testid={`duplicate-group-${key}`}>
                <div className="flex items-center justify-between gap-4 mb-3">
                  <div>
                    <h3 className="font-spectral text-2xl tracking-tight">
                      {g.members[0]?.name || g.name}
                    </h3>
                    <p className="text-xs text-[#6B6B6B]">
                      {g.breed} · born {formatDate(g.dob)} · {g.count} matching records
                    </p>
                  </div>
                  <Button
                    onClick={() => mergeAll(g)}
                    className="rounded-full bg-ink hover:bg-crimson text-white text-xs h-8 px-4"
                    data-testid={`duplicate-merge-all-${key}`}
                  >
                    <GitMerge size={14} className="mr-1" />
                    Merge all into canonical
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {g.members.map((m) => {
                    const isCanon = m.id === canonical;
                    return (
                      <div
                        key={m.id}
                        className={`rounded-lg border p-3 flex flex-col gap-2 ${
                          isCanon
                            ? "border-emerald-500 bg-emerald-50"
                            : "border-[#E5E5E5] bg-[#FAFAFA]"
                        }`}
                        data-testid={`duplicate-member-${m.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded bg-white border border-[#E5E5E5] flex items-center justify-center overflow-hidden flex-shrink-0">
                            {m.photos?.[0] ? (
                              <img src={thumbUrl(m.photos[0])} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <Dog size={18} className="text-[#B8B8B8]" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <Link
                              to={`/dogs/${m.id}`}
                              className="font-semibold text-sm hover:text-crimson truncate block"
                              target="_blank"
                              rel="noreferrer"
                            >
                              {m.name}
                            </Link>
                            <span className="text-[11px] text-[#6B6B6B] font-mono">
                              {m.registration_number || m.id.slice(0, 8)}
                            </span>
                          </div>
                          {isCanon && (
                            <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-500 text-white rounded px-1.5 py-0.5">
                              Canonical
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-[#6B6B6B]">
                          {m.color} · {m.sex}
                          {m.created_at ? ` · added ${m.created_at.slice(0, 10)}` : ""}
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          {!isCanon && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setCanonicalByGroup((s) => ({ ...s, [key]: m.id }))
                              }
                              className="rounded-full h-7 text-[11px] px-3 flex-1"
                              data-testid={`duplicate-set-canonical-${m.id}`}
                            >
                              Set canonical
                            </Button>
                          )}
                          {!isCanon && (
                            <Button
                              onClick={() => mergeOne(g, m.id)}
                              disabled={!!merging[m.id]}
                              size="sm"
                              className="rounded-full h-7 text-[11px] px-3 bg-crimson hover:bg-crimson/90 text-white flex-1"
                              data-testid={`duplicate-merge-${m.id}`}
                            >
                              <Trash size={11} className="mr-1" />
                              {merging[m.id] ? "Merging…" : "Merge → delete"}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
